import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { parseUrlParams, parseStartParam } from '../functions/startParam'
import { trackOnboardingCompleted } from '../utils/amplitude'

const ONBOARDING_KEY = 'onboarding_completed'

interface OnboardingContextType {
  isCompleted: boolean | null
  completeOnboarding: () => void
  resetOnboarding: () => void
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
)

interface OnboardingProviderProps {
  children: ReactNode
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const [isCompleted, setIsCompleted] = useState<boolean | null>(null)

  // Initialize state from localStorage on mount
  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY) === 'true'

    // If user came via referral link with box - skip onboarding
    // Only ref without box does not skip onboarding
    if (!completed) {
      // Use captured params from Index.tsx (before URL may be modified by router)
      // or fallback to current URL params
      const initialParams = window.__INITIAL_URL_PARAMS__
      const browserRef = initialParams?.ref ?? parseUrlParams().ref
      const browserBox = initialParams?.box ?? parseUrlParams().box

      // E2E tests can pass mock via global variable,
      // since @twa-dev/sdk overwrites window.Telegram on initialization
      const tgStartParam =
        window.__E2E_TELEGRAM_START_PARAM__ ??
        window.Telegram.WebApp.initDataUnsafe.start_param
      const { ref: telegramRef, box: telegramBox } =
        parseStartParam(tgStartParam)

      const hasRefWithBox =
        (browserRef && browserBox) || (telegramRef && telegramBox)

      if (hasRefWithBox) {
        localStorage.setItem(ONBOARDING_KEY, 'true')
        setIsCompleted(true)
        return
      }
    }

    setIsCompleted(completed)
  }, [])

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setIsCompleted(true)
    trackOnboardingCompleted()
  }

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY)
    setIsCompleted(false)
  }

  const value: OnboardingContextType = {
    isCompleted,
    completeOnboarding,
    resetOnboarding,
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext)

  if (context === undefined) {
    throw new Error(
      'useOnboardingContext must be used within an OnboardingProvider',
    )
  }
  return context
}
